/**
 * `W4-T09` — the handshake on the screens: what a refusal reads as, where a signed-out client's token
 * waits while they log in, and the one return path the login page may follow.
 *
 * Spec: `docs/specs/S4/W4-T09-b-the-handshake.md` §2.1, §4.4, §6.
 */
import { HANDSHAKE_TOKEN_PATTERN } from '@marketplace/contracts';
import { type TranslationKey } from '../../i18n/index.js';
import { ApiError } from '../../shared/api-error.js';

/** How often the professional's screen reads the state (§2.3: "poll, 3 s"). */
export const HANDSHAKE_POLL_MS = 3000;

/**
 * A refusal's code as a sentence (§6). `VALIDATION_FAILED` is a malformed code, which the client
 * reads as the code not matching; `FORBIDDEN` is an account that is not a client; `NOT_FOUND` is the
 * issue route's answer for a job the caller is not the provider of, which the professional reads as
 * the booking not being startable.
 */
export function handshakeErrorKey(code: string): TranslationKey {
  switch (code) {
    case 'HANDSHAKE_NOT_FOUND':
      return 'handshake.error.notFound';
    // `FORBIDDEN` is the permission matrix's refusal of a redeem: an account that is not a client at
    // all (§5), which is the professional scanning their own code on a provider-only account (test
    // plan §5.3).
    case 'HANDSHAKE_WRONG_PARTY':
    case 'FORBIDDEN':
      return 'handshake.error.wrongParty';
    case 'HANDSHAKE_EXPIRED':
      return 'handshake.error.expired';
    case 'HANDSHAKE_ALREADY_REDEEMED':
      return 'handshake.error.alreadyRedeemed';
    case 'HANDSHAKE_CODE_INVALID':
    case 'VALIDATION_FAILED':
      return 'handshake.error.codeInvalid';
    case 'HANDSHAKE_NOT_AVAILABLE':
    case 'NOT_FOUND':
      return 'handshake.error.notAvailable';
    case 'PAYMENTS_UNAVAILABLE':
      return 'booking.error.unavailable';
    default:
      return 'handshake.error.failed';
  }
}

/** A failed call as a key: its code when the API answered one, the generic sentence otherwise. */
export function handshakeRefusalKey(error: unknown): TranslationKey {
  return handshakeErrorKey(error instanceof ApiError ? error.message : 'UNKNOWN');
}

/**
 * The token from the QR's fragment, `#t=<token>` — or `null` when there is none or it is not one.
 * Checked with the contract's own pattern, so a mangled link never reaches the API.
 */
export function tokenFromHash(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, '')).get('t');
  return token !== null && HANDSHAKE_TOKEN_PATTERN.test(token) ? token : null;
}

/**
 * Where a signed-out client's token waits while they log in (§4.4). `sessionStorage`, so it dies with
 * the tab and never reaches a server; never the URL, which the login redirect would carry.
 */
const PENDING_TOKEN_KEY = 'handshake.pendingToken';

const storage = (): Storage | null =>
  typeof sessionStorage === 'undefined' ? null : sessionStorage;

export function rememberPendingToken(token: string): void {
  storage()?.setItem(PENDING_TOKEN_KEY, token);
}

export function pendingToken(): string | null {
  const token = storage()?.getItem(PENDING_TOKEN_KEY) ?? null;
  return token !== null && HANDSHAKE_TOKEN_PATTERN.test(token) ? token : null;
}

export function forgetPendingToken(): void {
  storage()?.removeItem(PENDING_TOKEN_KEY);
}

/**
 * The login page's `?next=`, when it is safe to follow: a path on this site, in the language the
 * client is reading, made of plain segments — no scheme, no `//` host, no `..`, no query and no
 * fragment, so it can neither leave the site nor carry a token. Anything else is `null`, and the login
 * page goes home as it always has.
 */
export function returnPathOf(next: string | null, locale: string): string | null {
  if (next === null || !/^[a-z]{2}$/.test(locale)) return null;
  return new RegExp(`^/${locale}(?:/[A-Za-z0-9_-]+)+$`).test(next) ? next : null;
}

/** The QR landing page's path for a signed-out client, sent to log in and back. */
export const startLoginPath = (locale: string): string =>
  `/${locale}/login?next=${encodeURIComponent(`/${locale}/start`)}`;
